<template>
    <div class="box-border pb-16 xxl:max-w-[1920px]">
        <h1 class="font-body-6 text-default-2 xxs:pl-4 lg:pl-0">News</h1>
        <div class="xxs:p-4 bg-white rounded border border-white lg:px-6 lg:pt-4 lg:pb-6 mt-4 box-border relative">
            <div v-if="(isAdmin || isContentAdmin) && isMobile" class="fixed z-50 bottom-4 right-4 left-auto min-w-[60px] h-[60px] rounded-full text-white bg-primary-1 flex items-center justify-center py-2 px-4 box-border group transition-all ease-in-out duration-200 cursor-pointer hover:min-w-[120px]">
                <router-link :to="'/news/create'" class="flex items-center whitespace-nowrap">
                    <span class="text-body-2 block -mr-2 group-hover:mr-0">+</span>
                    <p class="p-0 text-body-6 ml-2 mt-0 mb-0 mr-0 opacity-0 max-w-0 overflow-hidden group-hover:opacity-100 group-hover:max-w-[100px] transition-all ease-in-out duration-200">Add news</p>
                </router-link>
            </div>
            <div class="w-full flex flex-col box-border" v-if="loadingPage">
                <div class="w-full flex xxs:flex-col lg:flex-row mb-5">
                    <div class="skeleton-loader xxs:w-full lg:w-2/5 h-[42px]"></div>
                    <div class="skeleton-loader xxs:w-full md:w-full lg:w-1/4 h-[42px] xxs:mt-4 lg:ml-4 lg:mt-0"></div>
                    <div class="flex-1 flex">
                        <div class="skeleton-loader xxs:w-[49%] lg:w-[20%] h-[42px] xxs:ml-0 xxs:mt-4 lg:mt-0 lg:ml-4 xxs:mr-4"></div>
                        <div class="skeleton-loader flex-1 h-[42px] xxs:mt-4 lg:ml-4 lg:mt-0"></div>
                    </div>
                </div>
                <div class="w-full flex box-border justify-between flex-wrap preloader-wrap">
                    <div class="skeleton-loader xxs:w-full lg:w-[calc(33%-10px)] mt-5 h-[430px]" v-for="item in perPage" :key="item"></div>
                </div>
                <div class="flex justify-between w-full mt-6">
                    <div class="w-1/5 skeleton-loader h-[42px]" v-for="i in 3" :key="i"></div>
                </div>
            </div>
            <div class="w-full flex flex-col" v-else>
                <div v-if="hasNews" class="w-full flex flex-col items-end xxs:flex-wrap xl:flex-nowrap">
                    <div class="flex w-full justify-between xxs:flex-col md:flex-row ">
                        <CoreInput
                                v-model="search"
                                class="xxs:w-full md:w-[calc(50%-8px)] lg:w-1/2 xxs:order-last md:order-1 xxs:mt-4 md:mt-0"
                                hasSearchIcon
                                label="Search"
                                placeholder="Enter article subject"
                                @input="handleDebouncedSearch()"
                        >
                            <div class="absolute inset-y-0 right-0 pr-3 flex items-center h-10 cursor-pointer">
                                <img v-if="!search" alt="Search through news" src="@/assets/icons/search.svg"/>
                                <img v-else src="@/assets/icons/close-light.svg" @click="clearSearchInput" alt="clear"/>
                            </div>
                        </CoreInput>
                        <CoreButton v-if="(isAdmin || isContentAdmin) && !isMobile" buttonType="primary" class="xxs:w-full md:w-36 h-12 xxs:order-1 md:order-last" @click.prevent="router.push({name: 'create-news'})">
                            <span>+</span>
                            <span class="ml-2.5">Add news</span>
                        </CoreButton>
                    </div>
                    <div class="flex xxs:flex-col md:flex-row w-full mt-4">
                        <div class="flex flex-col xxs:w-full md:w-[calc(33%-8px)] xxs:mt-4 md:mt-0">
                            <p class="text-body-6 text-default-1 font-medium mb-1">Date</p>
                            <VueDatePicker
                                    :model-value="selectedDate"
                                    @update:model-value="handleDate"
                                    :max-date="maxDateBasedOnRoleAndFilters"
                                    range
                                    mode-height="120"
                                    auto-apply
                                    placeholder="Select date"
                                    :enable-time-picker="false"
                                    model-type="yyyy-MM-dd"
                                    class="mr-4"
                            />
                        </div>
                        <div v-if="isAdmin || isContentAdmin" class="flex flex-col xxs:w-full md:w-[calc(33%-8px)] xxs:mt-4 md:mt-0 md:ml-4">
                            <label class="text-body-6 font-medium text-default-1 mb-1">
                                Filters
                            </label>
                            <Multiselect
                                    mode="single"
                                    :canDeselect="false"
                                    placeholder="Enter a filter"
                                    :options="filters"
                                    v-model="selectedFilter"
                                    :closeOnSelect="false"
                                    :hideSelected="false"
                                    :clearOnSelect="false"
                                    :canClear="false"
                                    :searchable="true"
                                    @select="handleCategorySelection"
                            >
                                <template v-slot:option="{ option, isSelected }">
                                    <div class="w-5 h-5 -ml-1 flex-shrink-0">
                                        <Check :color="color.hex" class="h-5" v-if="isSelected(option)"/>
                                    </div>
                                    <span class="select-none relative -mb-px capitalize pl-2.5">
                                                    {{ option.label }}
                                                 </span>
                                </template>
                            </Multiselect>
                        </div>
                        <div class="flex flex-col xxs:w-full md:w-[calc(33%-8px)] xxs:mt-4 md:mt-0 md:ml-4">
                            <TagSelect2
                                    class="mlg:-mt-1.5"
                                    :model-value="selectedTags"
                                    @update:model-value="selectTags"
                                    :total-pages="totalTagPages"
                                    :options="tags"
                                    :createOption="canCreateTags"
                                    :infinite-loader="true"
                                    :close-on-select="true"
                                    label="Tags"
                                    @search-options="searchTags"
                                    @load-infinite="({ page, search }) => loadTags(page, search)"
                            />
                        </div>
                    </div>
                </div>

                <div class="w-full flex box-border flex-wrap justify-between preloader-wrap" v-if="loadingNews">
                    <div class="skeleton-loader xxs:w-full lg:w-[calc(33%-10px)] mt-5 h-[430px]" v-for="item in perPage" :key="item"></div>
                </div>

                <div class="flex w-full flex-wrap mt-1 justify-between" v-if="!loadingNews && news.length > 0">
                    <NewsCard
                        v-for="(article, index) in news"
                        :article="article"
                        :index="index"
                        :key="article.uid"
                        @articleDeleted="handleSearch"
                        @edit="() => router.push({name: 'edit-news', params: {uid: article.uid}})"
                        @toggle-like="toggleLike(index)"
                        @share="handleShare"
                        @view="() => router.push({name: 'news-article', params: {uid: article.uid}})"
                    />
                    <div class="xxs:hidden lg:block lg:w-[calc(33%-10px)]"></div>
                </div>
                <div v-if="emptyState" class="w-full h-[650px] flex justify-center items-center flex-col">
                    <NoBlogs :color="color.hex" />
                    <p class="mt-8 text-body-4 text-default-1">{{ emptyStateText }}</p>
                    <CoreButton v-if="(isAdmin || isContentAdmin) && !isMobile" buttonType="ghost" class="mt-10" @click="addNews">
                        <img src="../../assets/icons/plus.svg" alt="" />
                        <span class="ml-2.5">Add news</span>
                    </CoreButton>
                </div>
                <div class="flex w-full flex-col">
                    <Pagination
                        v-if="showPagination"
                        :from="meta.from ?? 0"
                        :last-page="meta.last_page"
                        :links="links"
                        :to="meta.to ?? 0"
                        :total="meta.total ?? 0"
                        @change="handlePaginationChange"
                    />
                </div>
            </div>
        </div>
    </div>
    <Modal v-if="showShareModal" :show="showShareModal" @close="closeShareModal" class="overflow-visible" :overflow-hidden="false">
        <Share
            v-model="showShareModal"
            type="news"
            :groups="articleToBeShared.groups"
            :members="articleToBeShared.members"
            :members-count="articleToBeShared.members_count"
            :groups-count="articleToBeShared.groups_count"
            :is-public-shared="articleToBeShared.public"
            :is-global-shared="articleToBeShared.global"
            @share="doShareArticle"
        />
    </Modal>
</template>

<script setup>
import {computed, nextTick, onMounted, ref} from "vue";

import Multiselect from "@/components/core/Multiselect.vue"
import CoreInput from "@/components/core/InputGroup.vue";
import CoreButton from "@/components/core/Button.vue";
import Pagination from "@/components/core/Pagination.vue";
import debounce from "lodash.debounce";
import VueDatePicker from "@vuepic/vue-datepicker";
import TagSelect2 from "@/components/core/TagSelect2.vue";
import NewsCard from "@/components/cards/NewsCard.vue";
import {useTheme} from "@/stores/ThemeStore";
import NoBlogs from "@/components/icons/NoBlogs.vue";
import {useRouter} from "vue-router";
import Check from "@/components/icons/Check.vue";
import {useAuth} from "@/stores/AuthStore";
import {useNews} from "@/composables/useNews";
import {useTags} from "@/composables/useTags";
import {storeToRefs} from "pinia";
import {useIsMobile} from "@/composables/useIsMobile";
import Modal from "@/components/core/Modal.vue";
import Share from "@/components/modals/partials/Share.vue";
import {toast} from "vue3-toastify";

const {getTags} = useTags();
const {getArticles, likeArticle, unlikeArticle, shareArticle} = useNews();
const {isAdmin, isContentAdmin} = useAuth();
const {organization} = storeToRefs(useAuth());
const {isMobile} = useIsMobile();

const router = useRouter();
const {color} = useTheme();

const loadingPage = ref(true);
const hasNews = ref(false);
const loadingNews = ref(false);

const search = ref('');
const filters = ref([{label: 'Published', value: 'published'}, {label: 'All News', value: 'all_news'}, {label: 'Scheduled', value: 'scheduled'}, {label: 'Liked', value: 'liked'}, {label: 'Trashed', value: 'bin'}]);
const selectedFilter = ref('published');

const tags = ref([]);
const selectedTags = ref([]);
const totalTagPages = ref(0);
const loadingTags = ref(false);
const totalTags = ref(0);

const canCreateTags = ref(false);
const selectedDate = ref(null);

const meta = ref(null)
const links = ref([]);
const perPage = 15;

const news = ref([]);

const showShareModal = ref(false);
const articleToBeShared = ref(null);

const handleDebouncedSearch = debounce(handleSearch, 300);

async function handleSearch(page = 1, perPage = 15) {
    loadingNews.value = true;

    const response = await getArticles(organization.value.slug, page, perPage, search.value, startDate.value, endDate.value, selectedFilter.value, selectedTags.value);
    news.value = response.data;
    meta.value = response.meta;
    links.value = response.links;

    loadingNews.value = false;
}

const handlePaginationChange = ({page, perPage}) => {
    handleSearch(page, perPage)
}

function clearSearchInput () {
    search.value = '';
    handleSearch();
}

function handleDate(date) {
    selectedDate.value = date;
    handleSearch();
}

function handleCategorySelection() {
    handleSearch();
}

function selectTags(tags) {
    selectedTags.value = tags
    canCreateTags.value = false;
    handleSearch();
}

function searchTags(searchQuery) {
    getTags(organization.value.slug, 1, 10, searchQuery)
        .then((response) => {
            console.log(response);
            totalTagPages.value = response.meta.last_page;
            totalTags.value = response.meta.total;

            if (totalTags.value === 0) {
                nextTick(() => {
                    canCreateTags.value = true;
                });
                tags.value = [];
                return;
            }

            tags.value = response.data.map((tag) => {
                return tag.name;
            });
        })
        .catch((error) => {
            console.error(error);
        });
}

function loadTags(page, searchQuery = '') {
    loadingTags.value = true;
    getTags(organization.value.slug, page, 10, searchQuery)
        .then((response) => {
            tags.value = [...tags.value, ...response.data.map(tag => tag.name)];
            totalTagPages.value = response.meta.last_page;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loadingTags.value = false;
        })
}

function toggleLike(index) {
    let article = news.value[index];
    if (article.is_liked) {
        unlikeArticle(organization.value.slug, article.uid)
            .then(() => {
                article.is_liked = false;
                article.total_likes--;
            })
            .catch((error) => {
                console.error(error);
            })
    } else {
        likeArticle(organization.value.slug, article.uid)
            .then(() => {
                article.is_liked = true;
                article.total_likes++;
            })
            .catch((error) => {
                console.error(error);
            })
    }
}

function handleShare(article) {
    showShareModal.value = true;
    articleToBeShared.value = article;
}

function closeShareModal() {
    showShareModal.value = false;
    articleToBeShared.value = null;
}

async function doShareArticle(data) {
    try {
        if(data.global && !articleToBeShared.value.tags.includes('Global News')) {
            articleToBeShared.value.tags.push('Global News');
        }
        if(!data.global && articleToBeShared.value.tags.includes('Global News')) {
            articleToBeShared.value.tags = articleToBeShared.value.tags.filter(tag => tag !== 'Global News');
        }
        data.tags = articleToBeShared.value.tags;
        const response = await shareArticle(organization.value.slug, articleToBeShared.value.uid, data);

        toast.success("The article was shared successfully.");

        news.value = news.value.map((article) => {
            if(article.uid === articleToBeShared.value.uid) {
                return {
                    ...article,
                    ...response
                }
            } else {
                return article;
            }
        });
    } catch (e) {
        console.log(e);
        toast.error("There was an error trying to share the article.");
    } finally {
        showShareModal.value = false;
        articleToBeShared.value = null;
    }
}

function addNews() {
    router.push({name: 'create-news'});
}

const emptyState = computed(() => {
    return news.value.length === 0 && !loadingNews.value;
});

const emptyStateText = computed(() => {
    return hasNews.value ? 'We couldn\'t find any news based on the searched filters' : 'There are no news added yet';
});

const showPagination = computed(() => {
    return (!loadingPage.value && hasNews.value && !emptyState.value && meta.value !== null);
});

const startDate = computed(() => {
    return selectedDate.value ? selectedDate.value[0] : '';
});

const endDate = computed(() => {
    return selectedDate.value ? selectedDate.value[1] : '';
});

const maxDateBasedOnRoleAndFilters = computed(() => {
    if(!isAdmin && !isContentAdmin) {
        return new Date();
    }

    let filterWithNoFutureDateSelection = ['published'];

    return !filterWithNoFutureDateSelection.includes(selectedFilter.value) ? null : new Date();
});

onMounted(async () => {
    loadTags(1);

    const response = await getArticles(organization.value.slug, 1, perPage, search.value, startDate.value, endDate.value, selectedFilter.value, selectedTags.value);

    news.value = response.data;
    meta.value = response.meta;

    links.value = response.links;

    loadingPage.value = false;
    if(news.value.length > 0) {
        hasNews.value = true;
    }
})
</script>

<style scoped>
    /deep/ .dp__input {
        @apply border-interactive-9;
        @apply text-body-6;
    }

    .preloader-wrap > .skeleton-loader:nth-child(3n+1) {
        @apply ml-0;
    }
</style>
